// ABOUTME: Modal for adding and editing data sources in workspace management.
// ABOUTME: Supports databricks, snowflake, bigquery, redshift, and postgres types.

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Database } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { dataSourceApi } from '@/services/api/adminClient';
import type { DataSource, DataSourceCreateRequest, DataSourceUpdateRequest, DataSourceType, DataSourceStatus } from '@/types/admin';

interface DataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  dataSource?: DataSource;
  onSaved: (dataSource: DataSource) => void;
}

const DATA_SOURCE_TYPES: DataSourceType[] = ['databricks', 'snowflake', 'bigquery', 'redshift', 'postgres'];
const DATA_SOURCE_STATUSES: DataSourceStatus[] = ['connected', 'error', 'disabled'];

export function DataSourceModal({
  isOpen,
  onClose,
  workspaceId,
  dataSource,
  onSaved,
}: DataSourceModalProps) {
  const isEditing = Boolean(dataSource);

  const [name, setName] = useState('');
  const [type, setType] = useState<DataSourceType>('databricks');
  const [host, setHost] = useState('');
  const [httpPath, setHttpPath] = useState('');
  const [catalog, setCatalog] = useState('');
  const [schemaName, setSchemaName] = useState('');
  const [status, setStatus] = useState<DataSourceStatus>('connected');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with data source values when editing
  useEffect(() => {
    if (dataSource) {
      setName(dataSource.name);
      setType(dataSource.type);
      setHost(dataSource.host);
      setHttpPath(dataSource.http_path || '');
      setCatalog(dataSource.catalog || '');
      setSchemaName(dataSource.schema_name || '');
      setStatus(dataSource.status);
    } else {
      resetForm();
    }
  }, [dataSource, isOpen]);

  const resetForm = () => {
    setName('');
    setType('databricks');
    setHost('');
    setHttpPath('');
    setCatalog('');
    setSchemaName('');
    setStatus('connected');
    setError(null);
  };

  const isFormValid = Boolean(name.trim() && host.trim());

  const handleSave = async () => {
    if (!name.trim() || !host.trim()) {
      setError('Name and host are required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let savedDataSource: DataSource;

      if (isEditing && dataSource) {
        const updateRequest: DataSourceUpdateRequest = {
          name: name !== dataSource.name ? name : undefined,
          host: host !== dataSource.host ? host : undefined,
          http_path: httpPath !== (dataSource.http_path || '') ? httpPath || undefined : undefined,
          catalog: catalog !== (dataSource.catalog || '') ? catalog || undefined : undefined,
          schema_name: schemaName !== (dataSource.schema_name || '') ? schemaName || undefined : undefined,
          status: status !== dataSource.status ? status : undefined,
        };

        savedDataSource = await dataSourceApi.update(workspaceId, dataSource.id, updateRequest);
      } else {
        const createRequest: DataSourceCreateRequest = {
          name: name.trim(),
          type,
          host: host.trim(),
          http_path: httpPath.trim() || undefined,
          catalog: catalog.trim() || undefined,
          schema_name: schemaName.trim() || undefined,
        };
        savedDataSource = await dataSourceApi.add(workspaceId, createRequest);
      }

      onSaved(savedDataSource);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data source');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-lg bg-surface-0 border border-surface-200 shadow-modal rounded-sm p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-success-light flex items-center justify-center">
                <Database className="w-5 h-5 text-success" />
              </div>
              <h2 className="text-xl font-semibold text-surface-800">
                {isEditing ? 'Edit Data Source' : 'Add Data Source'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-surface-100 rounded-sm hover:scale-105 transition-colors"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <div className="space-y-4 mb-6">
            <Input
              label="Name"
              placeholder="Production Database"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!name.trim() && error ? 'Name is required' : undefined}
            />

            <div>
              <label className="block text-sm font-medium text-surface-600 mb-1.5">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as DataSourceType)}
                disabled={isEditing}
                className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {DATA_SOURCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
              {isEditing && (
                <p className="text-xs text-surface-500 mt-1">
                  Type cannot be changed after creation
                </p>
              )}
            </div>

            <Input
              label="Host"
              placeholder="your-instance.cloud.databricks.com"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              error={!host.trim() && error ? 'Host is required' : undefined}
            />

            {(type === 'databricks') && (
              <Input
                label="HTTP Path"
                placeholder="/sql/1.0/warehouses/abc123"
                value={httpPath}
                onChange={(e) => setHttpPath(e.target.value)}
                helperText="Required for Databricks SQL warehouses"
              />
            )}

            <Input
              label="Catalog"
              placeholder="hive_metastore"
              value={catalog}
              onChange={(e) => setCatalog(e.target.value)}
              helperText="Optional: Default catalog to use"
            />

            <Input
              label="Schema"
              placeholder="default"
              value={schemaName}
              onChange={(e) => setSchemaName(e.target.value)}
              helperText="Optional: Default schema to use"
            />

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-surface-600 mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DataSourceStatus)}
                  className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                >
                  {DATA_SOURCE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isSaving}
              disabled={!isFormValid}
              className="flex-1"
              leftIcon={!isSaving ? <Database className="w-4 h-4" /> : undefined}
            >
              {isEditing ? 'Save Changes' : 'Add Data Source'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default DataSourceModal;
